"use client";

import { useState, useCallback } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import BottomSheet from "./BottomSheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import clsx from "clsx";

/**
 * Share component
 * Genel amaçlı paylaşma componenti
 * 
 * @param {string} url - Paylaşılacak URL (default: current page URL)
 * @param {string} title - Paylaşılacak başlık (default: document.title)
 * @param {string} text - Paylaşılacak açıklama metni
 * @param {Function} onShare - Paylaşma işlemi sonrası callback
 * @param {string} buttonClassName - Buton için ek class'lar
 * @param {boolean} showButton - Butonu göster/gizle (default: true)
 * @param {ReactNode} customButton - Özel buton componenti
 * @param {string} translationNamespace - i18n namespace (default: "common")
 */
export default function Share({
  url,
  title,
  text,
  onShare,
  buttonClassName = "",
  showButton = true,
  customButton,
  translationNamespace = "common",
}) {
  const isMobile = useIsMobile();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [copied, copy] = useCopyToClipboard();
  const t = useTranslationDetector(translationNamespace);

  // Mevcut sayfa URL'ini ve başlığını al
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || (typeof window !== "undefined" ? document.title : "");
  const shareText = text || shareTitle;

  // Web Share API kontrolü
  const isWebShareSupported = typeof navigator !== "undefined" && "share" in navigator;

  // Native share (Web Share API)
  const handleNativeShare = useCallback(async () => {
    if (!isWebShareSupported) {
      return;
    }

    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
      onShare?.("native");
    } catch (error) {
      // Kullanıcı paylaşmayı iptal etti veya hata oluştu
      if (error.name !== "AbortError") {
        console.error("Share failed:", error);
        // Hata durumunda fallback: mobilde bottom sheet aç, desktop'ta clipboard'a kopyala
        if (isMobile) {
          setIsBottomSheetOpen(true);
        } else {
          await copy(shareUrl);
        }
      }
    }
  }, [isWebShareSupported, shareTitle, shareText, shareUrl, copy, onShare, isMobile]);

  // Clipboard'a kopyala
  const handleCopyToClipboard = useCallback(async () => {
    await copy(shareUrl);
    if (isMobile) {
      setIsBottomSheetOpen(false);
    }
    onShare?.("clipboard");
  }, [copy, shareUrl, onShare, isMobile]);

  // Bottom sheet aç
  const handleOpenBottomSheet = useCallback(() => {
    setIsBottomSheetOpen(true);
  }, []);

  // Bottom sheet kapat
  const handleCloseBottomSheet = useCallback(() => {
    setIsBottomSheetOpen(false);
  }, []);

  // Buton click handler
  const handleButtonClick = useCallback(() => {
    if (isWebShareSupported) {
      // Web Share API destekleniyorsa direkt native share aç
      handleNativeShare();
    } else if (isMobile) {
      // Mobilde ve Web Share API desteklenmiyorsa bottom sheet aç
      handleOpenBottomSheet();
    } else {
      // Desktop'ta clipboard'a kopyala
      handleCopyToClipboard();
    }
  }, [isMobile, isWebShareSupported, handleNativeShare, handleOpenBottomSheet, handleCopyToClipboard]);

  // Özel buton varsa onu kullan
  if (customButton) {
    return (
      <>
        <div onClick={handleButtonClick}>{customButton}</div>
        {isMobile && (
          <BottomSheet
            isOpen={isBottomSheetOpen}
            onClose={handleCloseBottomSheet}
            title={t("share.title", { default: "Paylaş" })}
          >
            <div className="flex flex-col gap-4">
              {/* URL Copy */}
              <button
                onClick={handleCopyToClipboard}
                className={clsx(
                  "flex items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors",
                  "hover:bg-gray-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["link"] || byPrefixAndName.fas["link"]}
                      className="h-5 w-5 text-gray-700"
                    />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-gray-900">
                      {t("share.copyLink", { default: "Linki Kopyala" })}
                    </span>
                    {copied && (
                      <span className="text-xs text-green-600">
                        {t("share.copied", { default: "Kopyalandı!" })}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Share URL Preview */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-1">
                  {t("share.url", { default: "URL" })}
                </p>
                <p className="text-sm text-gray-900 break-all">{shareUrl}</p>
              </div>
            </div>
          </BottomSheet>
        )}
      </>
    );
  }

  // Varsayılan buton
  if (!showButton) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleButtonClick}
        className={clsx(
          "flex items-center justify-center",
          buttonClassName
        )}
        aria-label={t("share.button", { default: "Paylaş" })}
      >
        <FontAwesomeIcon
          icon={byPrefixAndName.fak["share-new"] || byPrefixAndName.far["arrow-up-from-bracket"] || byPrefixAndName.fas["share"]}
          className="text-[24px] max-w-[24px]"
        />
      </button>

      {isMobile && (
        <BottomSheet
          isOpen={isBottomSheetOpen}
          onClose={handleCloseBottomSheet}
          title={t("share.title", { default: "Paylaş" })}
        >
          <div className="flex flex-col gap-4">
            {/* URL Copy */}
            <button
              onClick={handleCopyToClipboard}
              className={clsx(
                "flex items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors",
                "hover:bg-gray-50"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["link"] || byPrefixAndName.fas["link"]}
                    className="h-5 w-5 text-gray-700"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-900">
                    {t("share.copyLink", { default: "Linki Kopyala" })}
                  </span>
                  {copied && (
                    <span className="text-xs text-green-600">
                      {t("share.copied", { default: "Kopyalandı!" })}
                    </span>
                  )}
                </div>
              </div>
            </button>

            {/* Share URL Preview */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-500 mb-1">
                {t("share.url", { default: "URL" })}
              </p>
              <p className="text-sm text-gray-900 break-all">{shareUrl}</p>
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
}
